import { config } from '../config';
import { round1 } from '../harness/stats';
import type { Summary } from '../harness/stats';
import type { AudioReport } from '../harness/audioScenario';
import type { AssemblyVariant, AudioRunRecord, ManualVerdict, SegmentRecord } from '../types';

/**
 * The U-1 results surface. Same brief as U-0's: legible on a phone, outdoors, in one glance.
 *
 * The order is the order of importance to the decision:
 *   1. ENV-1 admissibility — is this evidence at all?
 *   2. A-2 — does the assumption hold?
 *   3. per check: verdict, what was observed, what was expected
 *   4. per segment: what the probe said, and a Play control so the ear can overrule it
 *
 * The Play controls exist because the probe can be wrong in both directions. iOS is conservative
 * about loading media without user activation, so a `canplaythrough` that never fires does not
 * prove a file is broken — and a file that reports playable can still be silence. AUD-1's contract
 * is what a person hears, so the harness asks.
 *
 * Nothing here is a chart library (ENV-4 forbids third-party runtime scripts).
 */

const VARIANT_LABEL: Record<AssemblyVariant, string> = {
  full: 'Full — every chunk, in order',
  truncatedTail: 'Truncated — final chunk dropped (the hard-kill case)',
  firstChunkOnly: 'First chunk only — the shortest possible flag',
  withoutFirstChunk: 'Without the init fragment — negative control',
};

function Dist({ label, s, unit = 'ms' }: { label: string; s: Summary | null; unit?: string }) {
  return (
    <div className="figure">
      <div className="value">{s ? Math.round(s.p50) : '—'}</div>
      <div className="label">
        {label}
        {s && (
          <>
            <br />
            p95 {Math.round(s.p95)} · max {Math.round(s.max)} {unit} · n={s.n}
          </>
        )}
      </div>
    </div>
  );
}

export function AudioReportHeader({ report }: { report: AudioReport }) {
  const ok = report.gate.admissible;
  return (
    <>
      <div className={`gate ${ok ? 'ok' : 'bad'}`}>
        {ok
          ? 'ENV-1 SATISFIED — measured on the target device as an installed PWA.'
          : 'NOT ACCEPTANCE EVIDENCE (ENV-1)'}
        {(report.gate.blockers.length > 0 || report.gate.warnings.length > 0) && (
          <ul>
            {report.gate.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
            {report.gate.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
      <div className={`f9 ${report.a2.outcome === 'breached' ? 'breached' : report.a2.outcome}`}>
        <div className="t">{report.a2.headline}</div>
        <div className="d">{report.a2.detail}</div>
      </div>
    </>
  );
}

function SegmentCard({
  segment,
  onPlay,
  onVerdict,
  onDelete,
  busy,
}: {
  segment: SegmentRecord;
  onPlay: (segmentId: string, variant: AssemblyVariant) => void;
  onVerdict: (segmentId: string, verdict: ManualVerdict) => void;
  onDelete: (segmentId: string) => void;
  busy: boolean;
}) {
  const intervals = segment.chunks
    .map((c) => c.sinceLastMs)
    .filter((v): v is number => v !== null && Number.isFinite(v));
  const worst = intervals.length > 0 ? Math.max(...intervals) : null;

  return (
    <div className="card">
      <h2>
        {segment.label} · {segment.chunks.length} chunk(s)
      </h2>
      <p className="note">
        trigger <strong>{segment.trigger ?? '—'}</strong> · mime{' '}
        <strong>{segment.actualMime ?? '(none reported)'}</strong> · {segment.totalBytes.toLocaleString()} B ·{' '}
        {Number.isFinite(segment.wallDurationMs) ? `${Math.round(segment.wallDurationMs)} ms` : '—'} ·
        stamped at minute {segment.matchMinuteAtStart >= 0 ? segment.matchMinuteAtStart : '—'} (SES-10:
        derived at start)
        {worst !== null && ` · worst chunk gap ${Math.round(worst)} ms`}
        {!segment.complete && ' · RECOVERED, never finalized'}
      </p>

      <table className="checks">
        <tbody>
          {segment.probes.map((p) => (
            <tr key={p.variant}>
              <td>
                <span className={`badge ${p.canPlayThrough ? 'pass' : 'fail'}`}>
                  {p.canPlayThrough ? 'plays' : p.metadataLoaded ? 'metadata' : 'no'}
                </span>
              </td>
              <td>
                <strong>{VARIANT_LABEL[p.variant]}</strong>
                <br />
                <span className="note">
                  {p.chunkCount} chunk(s) · {p.sizeBytes.toLocaleString()} B · duration{' '}
                  {p.durationSeconds === null ? '—' : `${round1(p.durationSeconds)} s`} ·{' '}
                  {p.timedOut
                    ? `timed out at ${config.PLAYABILITY_PROBE_TIMEOUT_MS} ms`
                    : `probe ${Math.round(p.probeMs)} ms`}
                  {p.mediaErrorCode !== null && ` · MediaError ${p.mediaErrorCode}`}
                  {p.error && ` · ${p.error}`}
                </span>
              </td>
              <td>
                <button onClick={() => onPlay(segment.id, p.variant)} disabled={busy}>
                  Play
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="note" style={{ marginTop: 10 }}>
        What did you actually hear? This overrules the probe — AUD-1’s contract is playback.
      </p>
      <div className="row three">
        <button
          className={segment.manualVerdict === 'played' ? 'primary' : ''}
          onClick={() => onVerdict(segment.id, 'played')}
          disabled={busy}
        >
          Heard it
        </button>
        <button
          className={segment.manualVerdict === 'silent' ? 'danger' : ''}
          onClick={() => onVerdict(segment.id, 'silent')}
          disabled={busy}
        >
          Silence
        </button>
        <button
          className={segment.manualVerdict === 'refused' ? 'danger' : ''}
          onClick={() => onVerdict(segment.id, 'refused')}
          disabled={busy}
        >
          Wouldn’t play
        </button>
      </div>

      {segment.recorderReusable.attempted && (
        <p className="note">
          Recorder restart after this segment:{' '}
          <strong>{segment.recorderReusable.succeeded ? 'OK' : 'FAILED'}</strong>
          {segment.recorderReusable.message ? ` — ${segment.recorderReusable.message}` : ''}
        </p>
      )}
      {segment.notes && <p className="note">{segment.notes}</p>}

      <button className="danger" onClick={() => onDelete(segment.id)} disabled={busy}>
        Delete this segment
      </button>
    </div>
  );
}

export function AudioResultsView({
  report,
  run,
  onPlay,
  onVerdict,
  onDelete,
  busy,
}: {
  report: AudioReport;
  run: AudioRunRecord;
  onPlay: (segmentId: string, variant: AssemblyVariant) => void;
  onVerdict: (segmentId: string, verdict: ManualVerdict) => void;
  onDelete: (segmentId: string) => void;
  busy: boolean;
}) {
  return (
    <>
      <div className="card">
        <h2>Checks</h2>
        <table className="checks">
          <tbody>
            {report.checks.map((c) => (
              <tr key={c.id}>
                <td>
                  <span className={`badge ${c.verdict}`}>{c.verdict}</span>
                </td>
                <td>
                  <strong>{c.id}</strong> — {c.label}
                  <br />
                  <span className="note">{c.question}</span>
                  <br />
                  <span className="note">
                    observed: {c.observed}
                    <br />
                    expected: {c.expected}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2>Distributions</h2>
        <div className="figures">
          <Dist
            label={`chunk gap (asked ${config.TIMESLICE_MS} ms)`}
            s={report.chunkIntervalMs}
          />
          <Dist label="chunk size" s={report.chunkSizeBytes} unit="B" />
          <Dist label="cold getUserMedia" s={report.coldAcquireMs} />
          <Dist label="warm tap → recording" s={report.warmTapToRecordingMs} />
        </div>
        <p className="note">
          AUD-3 triggers exercised: <strong>{report.triggersCovered.join(', ') || 'none'}</strong>
          {report.triggersMissing.length > 0 && (
            <>
              {' '}
              · still missing: <strong>{report.triggersMissing.join(', ')}</strong> — the run has not
              tested AUD-3 until these have happened.
            </>
          )}
        </p>
      </div>

      <div className="card">
        <h2>Observations</h2>
        <ul>
          {report.observations.map((o) => (
            <li key={o.id} className="note">
              {o.text}
            </li>
          ))}
        </ul>
      </div>

      {run.segments.map((s) => (
        <SegmentCard
          key={s.id}
          segment={s}
          onPlay={onPlay}
          onVerdict={onVerdict}
          onDelete={onDelete}
          busy={busy}
        />
      ))}

      <div className="card">
        <h2>Device &amp; capability</h2>
        <dl className="kv">
          <dt>user agent</dt>
          <dd>{run.environment.userAgent}</dd>
          <dt>installed PWA</dt>
          <dd>{String(run.environment.standalone)}</dd>
          <dt>MediaRecorder</dt>
          <dd>{String(run.capabilities.mediaRecorderSupported)}</dd>
          <dt>mic permission</dt>
          <dd>{run.capabilities.permissionState}</dd>
          <dt>default mime</dt>
          <dd>
            {run.capabilities.defaultMime ??
              '— the browser reports nothing before a recording starts (spec-legal, and what Chromium does). The authoritative value is the segment mime below.'}
          </dd>
          <dt>recorded mime</dt>
          <dd>
            {[...new Set(run.segments.map((s) => s.actualMime).filter(Boolean))].join(', ') ||
              '— record a segment to find out'}
          </dd>
          <dt>isTypeSupported</dt>
          <dd>
            {run.capabilities.supportMatrix.map((m) => (
              <div key={m.mime}>
                {m.supported ? '✓' : '✗'} {m.mime}
              </div>
            ))}
          </dd>
          <dt>storage</dt>
          <dd>
            {run.storage.usageBytes ?? '—'} B of {run.storage.quotaBytes ?? '—'} B · persisted=
            {String(run.storage.persistedAfter)}
          </dd>
        </dl>
        {run.failures.length > 0 && (
          <ul>
            {run.failures.map((f, i) => (
              <li key={`${f.stage}-${i}`} className="note">
                {f.stage}: {f.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
